import { useMemo } from 'react';
import { Form } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { DateField } from '@/components/common';
import { unitSymbol } from '@/lib/mappers/units';
import {
  buildYesNoOptions,
  buildChainOfCustodyOptions,
  buildDeclarationCountryOptions,
  buildRawMaterialTypeOptions,
  buildProductTypeOptions,
} from '../../constants/editDeclarationOptions';
import { DeclarationTextField, DeclarationSelectField } from './declarationFields';

// Step 2 — the material the declaration covers, and the scope its raw material is certified under.
export const MaterialsDetailsStep = () => {
  const t = useTranslations();

  const yesNoOptions = useMemo(() => buildYesNoOptions(t), [t]);
  const chainOfCustodyOptions = useMemo(() => buildChainOfCustodyOptions(t), [t]);
  const countryOptions = useMemo(() => buildDeclarationCountryOptions(), []);
  const rawMaterialTypeOptions = useMemo(() => buildRawMaterialTypeOptions(), []);
  const productTypeOptions = useMemo(() => buildProductTypeOptions(), []);

  return (
    <Form.Row columns={2}>
      <Form.Section title={t('SECTION_GENERAL_INFORMATION')}>
        <DeclarationSelectField
          name="typeOfProduct"
          label={t('FIELD_TYPE_OF_PRODUCT')}
          options={productTypeOptions}
          required
        />
        <DeclarationSelectField
          name="rawMaterialType"
          label={t('FIELD_RAW_MATERIAL_TYPE')}
          options={rawMaterialTypeOptions}
          required
        />
        <DeclarationTextField
          name="additionalInformation"
          label={t('ALLOCATIONS_FIELD_ADDITIONAL_INFORMATION')}
        />
        <DeclarationSelectField
          name="countryOfOrigin"
          label={t('FIELD_COUNTRY_OF_ORIGIN')}
          options={countryOptions}
          required
          searchable
        />

        <Form.Row columns={2}>
          {/* The allocation fixes both — the declaration cannot certify a different amount. */}
          <DeclarationTextField
            name="totalCertifiedQuantity"
            label={t('ALLOCATIONS_FIELD_TOTAL_CERTIFIED_QUANTITY')}
            required
            readOnly
          />
          <DeclarationTextField
            name="unit"
            label={t('FIELD_UNIT')}
            required
            readOnly
            format={unitSymbol}
          />
        </Form.Row>

        <DeclarationTextField
          name="energyContent"
          label={t('ALLOCATIONS_FIELD_ENERGY_CONTENT')}
          required
        />

        <Form.Row columns={2}>
          <DeclarationSelectField
            name="isccCompliantMaterial"
            label={t('ALLOCATIONS_FIELD_ISCC_COMPLIANT')}
            options={yesNoOptions}
            required
          />
          <DeclarationSelectField
            name="euRedCompliantMaterial"
            label={t('ALLOCATIONS_FIELD_EU_RED_COMPLIANT')}
            options={yesNoOptions}
            required
          />
        </Form.Row>

        <Form.Row columns={2}>
          <DeclarationSelectField
            name="chainOfCustodyOption"
            label={t('ALLOCATIONS_FIELD_CHAIN_OF_CUSTODY')}
            options={chainOfCustodyOptions}
            required
          />
          <DeclarationSelectField
            name="countryOfBiofuelProduction"
            label={t('ALLOCATIONS_FIELD_COUNTRY_OF_BIOFUEL')}
            options={countryOptions}
            searchable
            required
          />
        </Form.Row>

        <Form.Row columns={2}>
          <DateField
            fieldName="startDateOfBiofuelProduction"
            label={t('ALLOCATIONS_FIELD_START_DATE_BIOFUEL')}
            required
          />
          <DateField
            fieldName="startDateOfBioliquidUse"
            label={t('ALLOCATIONS_FIELD_START_DATE_BIOLIQUID')}
          />
        </Form.Row>
      </Form.Section>

      <Form.Section title={t('SECTION_CERTIFICATION_SCOPE')}>
        <DeclarationSelectField
          name="sustainabilityCriteriaCompliance"
          label={t('ALLOCATIONS_FIELD_SUSTAINABILITY_CRITERIA')}
          options={yesNoOptions}
          required
        />
        <DeclarationSelectField
          name="intermediateCrop"
          label={t('ALLOCATIONS_FIELD_INTERMEDIATE_CROP')}
          options={yesNoOptions}
        />
        <DeclarationSelectField
          name="lowIlucRisk"
          label={t('ALLOCATIONS_FIELD_LOW_ILUC_RISK')}
          options={yesNoOptions}
        />
        <DeclarationSelectField
          name="wasteOrResidue"
          label={t('ALLOCATIONS_FIELD_WASTE_OR_RESIDUE')}
          options={yesNoOptions}
          required
        />
        <DeclarationTextField
          name="wastePermitNumber"
          label={t('ALLOCATIONS_FIELD_WASTE_PERMIT_NUMBER')}
        />
        <DeclarationSelectField
          name="supportReceived"
          label={t('ALLOCATIONS_FIELD_SUPPORT_RECEIVED')}
          options={yesNoOptions}
          required
        />
        <DeclarationTextField
          name="supportNatureAndScheme"
          label={t('ALLOCATIONS_FIELD_SUPPORT_NATURE')}
        />
      </Form.Section>
    </Form.Row>
  );
};
